import { module } from 'modujs';
import { isDebug } from '../utils/environment';


const lang_ids = {
    'fr': 1,
    'en': 2,
    'iu': 3
}

const lang_formats = {
    'fr': 'fr-CA',
    'en': 'en-US',
    'iu': 'en-US'
}

const AWB_RESPONSE_OK = 'ok';

/**
 * A number, or a string containing a number.
 *
 * @typedef {(number|string)} NumberLike
 */

/**
 * A string representation of a date/time.
 *
 * @typedef {string} DateLike
 */

/**
 * A string representation of a time.
 *
 * @typedef {string} TimeLike
 */

/**
 * A rendered template.
 *
 * @typedef {string} Template
 */

/**
 * A tracking number.
 *
 * @typedef {(number|string)} TrackingNumber
 */

/**
 * The AWB API response wrapper object.
 *
 * @typedef {object} AWBResponseWrapper
 *
 * @property {TrackingNumber}            number
 * @property {AWBResponse|TrackingError} response
 * @property {Error}                     [error]
 */

/**
 * A tracking error object.
 *
 * @typedef {(Error|string)} TrackingError
 */

/**
 * The AWB API response object.
 *
 * @typedef {object} AWBResponse
 *
 * @property {string}         status - AWB response status (should be "ok").
 * @property {TrackerDetails} data   - Tracking details.
 */

/**
 * The tracked shipment details.
 *
 * @typedef {object} TrackerDetails
 *
 * @property {string}            AwbPrefix
 * @property {ShipmentDetails[]} CommoditiesByNatureOfGoods
 * @property {DateLike}          DateLastPickedUp
 * @property {DateLike}          DateLastReceived
 * @property {DateLike}          DateLasteOrigin
 * @property {TrackingNumber}    FormNumber
 * @property {number}            OriginalPieces
 * @property {NumberLike}        OriginalWeightKgs
 * @property {number}            nTotalOrigin
 * @property {number}            nTotalPieceMvment
 * @property {number}            nTotalPieceRamasse
 * @property {number}            nTotalPieceRecu
 * @property {NumberLike}        rPoidsMvmt
 * @property {NumberLike}        rPoidsOrigin
 * @property {NumberLike}        rPoidsRamasse
 * @property {NumberLike}        rPoidsRecu
 * @property {string}            UltimateDestination
 * @property {string}            UltimateOrigin
 */

/**
 * The tracked shipment details.
 *
 * @typedef {object} ShipmentDetails
 *
 * @propety {ShipmentHistory[]} HistoriqueNew
 * @propety {NumberLike}        m_nTotalKgs
 * @propety {number}            m_nTotalPiece
 * @propety {string}            m_sMarchandiseInterne
 * @propety {NumberLike}        nTotalOrigin
 * @propety {number}            nTotalPieceEnTransit
 * @propety {number}            nTotalPieceRamasse
 * @propety {number}            nTotalPieceRecu
 * @propety {NumberLike}        rPoidsEnTransit
 * @propety {NumberLike}        rPoidsOring
 * @propety {NumberLike}        rPoidsRamasse
 * @propety {NumberLike}        rPoidsRecu
 * @propety {ShipmentState[]}   ShipmentRegroupe
 */

/**
 * The tracked shipment state.
 *
 * @typedef {object} ShipmentState
 *
 * @property {string}     Destination
 * @property {string}     DestinationName
 * @property {DateLike}   FinalledDateTime
 * @property {NumberLike} Kgs
 * @property {string}     LegRefCode
 * @property {number}     LegRefNo
 * @property {string}     Origin
 * @property {string}     OriginName
 * @property {string}     Pieces
 * @property {string}     StatusDetails
 * @property {string}     StatusID
 */

/**
 * The tracked shipment history.
 *
 * @typedef {object} ShipmentHistory
 *
 * @property {number}     m_nPiece
 * @property {NumberLike} m_rKg
 * @property {DateLike}   m_sDateHistory
 * @property {TimeLike}   m_sHeureHistory
 * @property {object}     oSentence
 * @property {string}     oSentence.m_Sentence
 * @property {string}     statuID
 */

/**
 * Shipment Tracker
 *
 * Test with 45167695 and 14544913.
 */
export default class extends module {
    constructor(m) {
        super(m);

        this.events = {
            click: {
                button: 'toggleDetails'
            }
        };

        /** @type {Intl.DateTimeFormat} */
        this.dateTimeFormtter;

        /** @type {object} */
        this.translations = JSON.parse(this.getData('translations'));
    }

    init() {
        const searchParams    = new URLSearchParams(window.location.search);
        const trackingNumbers = this.parseTrackingNumbers(
            // 'trackingNumber' replaces 'id'
            (searchParams.get('trackingNumber') || searchParams.get('id'))
        );

        if (trackingNumbers.length) {
            /** @type {Promise<AWBResponseWrapper>[]} */
            const requests = [];

            trackingNumbers.forEach((number) => requests.push(this.fetchData(number)));

            /**
             * @param  {AWBResponseWrapper[]} responses
             * @return {Promise<AWBResponseWrapper[]>}
             */
            Promise.all(requests).then((responses) => this.render(responses));
        } else {
            this.render([]);
        }
    }

    /**
     * @param  {*} numbers - The value to parse.
     * @return {TrackingNumber[]}
     */
    parseTrackingNumbers(numbers) {
        if (!numbers) {
            return [];
        }

        switch (typeof numbers) {
            case 'string':
                numbers = numbers.split(/\s*,+\s*|[\r\n|\r|\n]+/);
                break;

            case 'number':
                numbers = [ numbers ];
                break;

            default:
                if (Array.isArray(numbers) && numbers.length > 0) {
                    break;
                }

                return [];
        }

        return numbers
            .map((number) => {
                switch (typeof number) {
                    case 'string':
                        return number.trim();

                    case 'number':
                        return number;
                }

                return null;
            })
            .filter((number) => number);
    }

    /**
     * @param  {TrackingNumber} number - The tracking number to process.
     * @return {Promise<AWBResponseWrapper>}
     */
    fetchData(number) {
        const locale = lang_ids[this.getLang()];

        return new Promise((resolve) => {
            fetch(`/cargo.php?id=${number}&lang=${locale}`)
                /**
                 * @param  {Response} response - The Fetch API Response interface.
                 * @return {Promise<AWBResponse>}
                 */
                .then((response) => response.json().catch(() => response.text()))
                /**
                 * @param  {AWBResponse} response - The AWB API response body.
                 * @return {Promise<AWBResponseWrapper<AWBResponse>>}
                 */
                .then((response) => resolve({
                    number,
                    response
                }))
                /**
                 * @param  {TrackingError} error
                 * @return {Promise<AWBResponseWrapper<TrackingError>>}
                 */
                .catch((error) => resolve({
                    number,
                    response: error
                }));
        });
    }

    /**
     * @param  {DateLike} date - The date/time string to format.
     * @return {?string} The formatted date/time string or NULL.
     */
    formatDate(date) {
        // Dirty date format fix for safari
        date = date.replace('t','T');

        if (date) {
            return this.getDateTimeFormtter().format(new Date(date));
        }

        return null;
    }

    /**
     * @param  {number} progress
     * @return {number}
     */
    calculateGauge(progress) {
        return progress * 360 / 1;
    }

    /**
     * @param {AWBResponseWrapper[]} wrappers - Zero or more AWB API response wrappers.
     */
    render(wrappers) {
        isDebug && console.group('[App.Cargo.render]', 'Tracking:', wrappers.length);

        if (wrappers.length) {
            /**
             * @param  {AWBResponseWrapper}   wrapper
             * @param  {number}               index
             * @param  {AWBResponseWrapper[]} wrappers
             * @return {?Template}
             */
            this.el.innerHTML = wrappers.map((wrapper, index) => {
                try {
                    this.assertAWBResponseOk(wrapper.response);

                    return this.renderShipment(wrapper, index, wrappers);
                } catch (error) {
                    if (wrapper.response !== error) {
                        wrapper.error = error;
                    }

                    return this.renderShipmentNotFound(wrapper, index, wrappers);
                }
            }).join('');
        } else {
            this.el.innerHTML = this.renderTrackingResponseNotFound();
        }


        isDebug && console.groupEnd();
    }

    /**
     * @param  {*} response
     * @return {boolean}
     */
    isAWBResponse(response) {
        return (
            typeof response === 'object' &&
            response != null &&
            response.data &&
            response.status
        );
    }

    /**
     * @param  {AWBResponse} response
     * @return {boolean}
     */
    isAWBResponseOk(response) {
        return (
            response.status &&
            response.status.toLowerCase() === AWB_RESPONSE_OK
        );
    }

    /**
     * @param  {*} response
     * @throws {Error} If the response is not OK, is invalid, or is already an error.
     * @return {void}
     */
    assertAWBResponseOk(response) {
        if (response instanceof Error) {
            throw response;
        }

        if (!this.isAWBResponse(response)) {
            throw new Error('AWB Response is invalid');
        }

        if (!this.isAWBResponseOk(response)) {
            throw new Error('AWB Response is not OK');
        }
    }

    /**
     * @param  {AWBResponseWrapper}   wrapper
     * @param  {TrackingNumber}       wrapper.number
     * @param  {AWBResponse}          wrapper.response
     * @param  {number}               [index]
     * @param  {AWBResponseWrapper[]} [responses]
     * @return {?Template}
     */
    renderShipment({ number, response }, index, responses) {
        isDebug && console.group('[App.Cargo.renderShipment]', number);
        if (isDebug) {
            response && console.log('Response:', response);
        }

        const originProgress   = ((response.data.nTotalOrigin + response.data.nTotalPieceMvment + response.data.nTotalPieceRecu + response.data.nTotalPieceRamasse) / response.data.OriginalPieces);
        const transitProgress  = ((response.data.nTotalPieceMvment + response.data.nTotalPieceRecu + response.data.nTotalPieceRamasse) / response.data.OriginalPieces);
        const receivedProgress = ((response.data.nTotalPieceRecu + response.data.nTotalPieceRamasse) / response.data.OriginalPieces);
        const pickedUpProgress = (response.data.nTotalPieceRamasse / response.data.OriginalPieces);

        const html = `
            <header class="o-section -margin-medium -border">
                <div class="o-section_container">
                    <p class="c-heading -h6">${this.translations['parcel-tracking.your-shipment']}</p>
                    <h1 class="c-header-page_heading -margin || c-heading -h2">
                        ${response.data.AwbPrefix}${response.data.FormNumber}
                    </h1>
                    <div class="o-layout">
                        <div class="o-layout_item u-5/14@from-small">
                            <ul class="c-list-info">
                                <li class="c-list-info_item">
                                    <span class="c-list-info_title">${this.translations['parcel-tracking.origin']}</span>
                                    <span>${response.data.UltimateOrigin}</span>
                                </li>
                                <li class="c-list-info_item">
                                    <span class="c-list-info_title">${this.translations['parcel-tracking.destination']}</span>
                                    <span>${response.data.UltimateDestination}</span>
                                </li>
                                <li class="c-list-info_item">
                                    <span class="c-list-info_title">${this.translations['parcel-tracking.quantity']}</span>
                                    <span>${response.data.OriginalPieces}&nbsp;${this.translations['parcel-tracking.piece-unit']} / ${response.data.OriginalWeightKgs}&nbsp;kg</span>
                                </li>
                                <!--<li class="c-list-info_item">
                                    <span class="c-list-info_title">${this.translations['parcel-tracking.status']}</span>
                                    <span>
                                    ${response.data.DateLastPickedUp
                                        ? `${this.translations['parcel-tracking.picked-up-at']} ${response.data.UltimateDestination}&nbsp;${response.data.nTotalPieceRamasse >= response.data.OriginalPieces ? `<svg aria-hidden="true" focusable="false"><use xlink:href="assets/images/sprite.svg#checkmark"></use></svg>` : ''}`
                                        : response.data.DateLastReceived
                                            ? `${this.translations['parcel-tracking.received-at-airport']} ${response.data.UltimateDestination}&nbsp;<svg aria-hidden="true" focusable="false"><use xlink:href="assets/images/sprite.svg#map-pin-stroke"></use></svg>`
                                            : transitProgress > 0
                                                ? `${this.translations['parcel-tracking.in-transit']}&nbsp;<svg aria-hidden="true" focusable="false"><use xlink:href="assets/images/sprite.svg#plane"></use></svg>`
                                                : `${this.translations['parcel-tracking.accepted-at']} ${response.data.UltimateOrigin}&nbsp;<svg aria-hidden="true" focusable="false"><use xlink:href="assets/images/sprite.svg#map-pin-stroke"></use></svg>`}
                                    </span>
                                </li>-->
                            </ul>
                        </div>
                        <div class="o-layout_item u-4/14@from-small"></div>
                        <div class="o-layout_item u-5/14@from-small"></div>
                    </div>
                </div>
            </header>
            <div class="o-section" data-cargo="section">
                <div class="u-relative">
                    <section class="o-section_white -small-padding-top-bottom">
                        <div class="o-layout -gutter-tiny">
                            <div class="o-layout_item u-1/5@from-small">
                                <h2 class="c-heading -medium -no-margin@from-small">${this.translations['parcel-tracking.overview']}</h2>
                            </div>
                            <div class="o-layout_item u-4/5@from-small">
                                <div class="c-progress">
                                    <div class="c-progress_item -state${response.data.DateLasteOrigin || originProgress > 0 ? ' is-active' : ''}${originProgress >= 1 ? ' is-done' : ''}">
                                        <div class="c-progress_state">
                                            <div class="c-progress_icon">
                                                <svg viewBox="0 0 120 120" class="c-progress_icon_gauge">
                                                    <circle class="c-progress_icon_gauge_circle" transform="rotate(-90 60 60)" stroke-dasharray="${this.calculateGauge(originProgress)} 360" stroke-dashoffset="320" r="57.32" cx="60" cy="60"></circle>
                                                </svg>
                                                <svg aria-hidden="true" focusable="false"><use xlink:href="assets/images/sprite.svg#map-pin-stroke"></use></svg>
                                            </div>
                                        </div>
                                        <h3 class="c-progress_heading c-heading -h5">${this.translations['parcel-tracking.accepted-at']} ${response.data.UltimateOrigin}</h3>
                                        <time>${this.formatDate(response.data.DateLasteOrigin)}</time>
                                        ${response.data.rPoidsOrigin
                                            ? `<div class="c-progress_weight">${response.data.nTotalOrigin}&nbsp;${this.translations['parcel-tracking.piece-unit']} / ${response.data.rPoidsOrigin}&nbsp;kg</div>`
                                            : `<div class="c-progress_weight -hidden"></div>`}
                                    </div>
                                    <div class="c-progress_item -state${transitProgress > 0 ? ' is-active' : ''}${transitProgress >= 1 && response.data.nTotalPieceMvment == 0 ?' is-done' : ''}">
                                        <div class="c-progress_state">
                                            <div class="c-progress_icon">
                                                <svg viewBox="0 0 120 120" class="c-progress_icon_gauge">
                                                    <circle class="c-progress_icon_gauge_circle" transform="rotate(-90 60 60)" stroke-dasharray="${this.calculateGauge(transitProgress)} 360" stroke-dashoffset="320" r="57.32" cx="60" cy="60"></circle>
                                                </svg>
                                                <svg aria-hidden="true" focusable="false"><use xlink:href="assets/images/sprite.svg#plane"></use></svg>
                                            </div>
                                        </div>
                                        <h3 class="c-progress_heading c-heading -h5">${this.translations['parcel-tracking.in-transit']}</h3>
                                        <time></time>
                                        ${response.data.rPoidsMvmt
                                            ? `<div class="c-progress_weight">${response.data.nTotalPieceMvment}&nbsp;${this.translations['parcel-tracking.piece-unit']} / ${response.data.rPoidsMvmt}&nbsp;kg</div>`
                                            : `<div class="c-progress_weight -hidden"></div>`}
                                    </div>
                                    <div class="c-progress_item -state${receivedProgress > 0 ? ' is-active' : ''}${receivedProgress >= 1 ?' is-done' : ''}">
                                        <div class="c-progress_state">
                                            <div class="c-progress_icon">
                                                <svg viewBox="0 0 120 120" class="c-progress_icon_gauge">
                                                    <circle class="c-progress_icon_gauge_circle" transform="rotate(-90 60 60)" stroke-dasharray="${this.calculateGauge(receivedProgress)} 360" stroke-dashoffset="320" r="57.32" cx="60" cy="60"></circle>
                                                </svg>
                                                <svg aria-hidden="true" focusable="false"><use xlink:href="assets/images/sprite.svg#map-pin-stroke"></use></svg>
                                            </div>
                                        </div>
                                        <h3 class="c-progress_heading c-heading -h5">${this.translations['parcel-tracking.received-at-airport']} ${response.data.UltimateDestination}</h3>
                                        <time>${this.formatDate(response.data.DateLastReceived)}</time>
                                        ${response.data.rPoidsRecu
                                            ? `<div class="c-progress_weight">${response.data.nTotalPieceRecu}&nbsp;${this.translations['parcel-tracking.piece-unit']} / ${response.data.rPoidsRecu}&nbsp;kg</div>`
                                            : `<div class="c-progress_weight -hidden"></div>`}
                                    </div>
                                    <div class="c-progress_item -state${pickedUpProgress > 0 ? ' is-active' : ''}${pickedUpProgress >= 1 ?' is-done' : ''}">
                                        <div class="c-progress_state">
                                            <div class="c-progress_icon">
                                                <svg viewBox="0 0 120 120" class="c-progress_icon_gauge">
                                                    <circle class="c-progress_icon_gauge_circle" transform="rotate(-90 60 60)" stroke-dasharray="${this.calculateGauge(pickedUpProgress)} 360" stroke-dashoffset="320" r="57.32" cx="60" cy="60"></circle>
                                                </svg>
                                                <svg aria-hidden="true" focusable="false"><use xlink:href="assets/images/sprite.svg#checkmark"></use></svg>
                                            </div>
                                        </div>
                                        <h3 class="c-progress_heading c-heading -h5">${this.translations['parcel-tracking.picked-up-at']} ${response.data.UltimateDestination}</h3>
                                        <time>${this.formatDate(response.data.DateLastPickedUp)}</time>
                                        ${response.data.rPoidsRamasse
                                            ? `<div class="c-progress_weight">${response.data.nTotalPieceRamasse}&nbsp;${this.translations['parcel-tracking.piece-unit']} / ${response.data.rPoidsRamasse}&nbsp;kg</div>`
                                            : `<div class="c-progress_weight -hidden"></div>`}
                                    </div>
                                </div>
                                ${responses.length > 1 ? `
                                    <br>
                                    <button class="c-button -primary -small" data-cargo="button">
                                        <span>${this.translations['parcel-tracking.shipment-details']}</span>
                                        <svg class="c-button_icon -right -visible" aria-hidden="true" focusable="false"><use xlink:href="assets/images/sprite.svg#more"></use></svg>
                                        <svg class="c-button_icon -hidden" aria-hidden="true" focusable="false"><use xlink:href="assets/images/sprite.svg#minus"></use></svg>
                                    </button>
                                ` : ''}
                            </div>
                        </div>
                    </section>

                    ${response.data.CommoditiesByNatureOfGoods.length > 0 && `
                    <div class="o-section_more${responses.length > 1 ? ` -collapse` : ''}" data-cargo="more">
                        <div class="o-section_more_wrap">
                            <div aria-hidden="true" class="o-section_white_separator">
                                <svg focusable="false"><use xlink:href="assets/images/sprite.svg#ticket-hole"></use></svg>
                                <span></span>
                                <svg focusable="false"><use xlink:href="assets/images/sprite.svg#ticket-hole"></use></svg>
                            </div>

                            <section class="o-section_white -small-padding-bottom-top">
                                <div class="o-layout -middle">
                                    <div class="o-layout_item u-1/5@from-small">
                                        <h2 class="c-heading -medium -no-margin">${this.translations['parcel-tracking.shipment-details']}</h2>
                                    </div>
                                    <div class="o-layout_item u-4/5@from-small">
                                        <div class="c-progress -heading">
                                            <div class="c-progress_item">
                                                <p class="c-progress_heading c-heading -h5 -no-margin">${this.translations['parcel-tracking.status.accepted']}</p>
                                            </div>
                                            <div class="c-progress_item">
                                                <p class="c-progress_heading c-heading -h5 -no-margin">${this.translations['parcel-tracking.status.transit']}</p>
                                            </div>
                                            <div class="c-progress_item">
                                                <p class="c-progress_heading c-heading -h5 -no-margin">${this.translations['parcel-tracking.status.received']}</p>
                                            </div>
                                            <div class="c-progress_item">
                                                <p class="c-progress_heading c-heading -h5 -no-margin">${this.translations['parcel-tracking.status.picked-up']}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    ${response.data.CommoditiesByNatureOfGoods.map((commodity) => `
                                        <div class="c-progress -product" data-progress="section">
                                            <div class="c-progress_main">
                                                <div class="c-progress_header">
                                                    <div class="c-progress_header_icon c-circled-icon">
                                                        <svg aria-hidden="true" focusable="false"><use xlink:href="assets/images/sprite.svg#box"></use></svg>
                                                    </div>
                                                    <div>
                                                        <h4 class="c-progress_header_title c-heading -h4 -light">
                                                            ${commodity.m_sMarchandiseInterne}
                                                        </h4>
                                                        <p>${commodity.m_nTotalPiece}&nbsp;${this.translations['parcel-tracking.piece-unit']} / ${commodity.m_nTotalKgs}&nbsp;kg</p>
                                                        ${commodity.HistoriqueNew.length > 0 ? `
                                                        <button class="c-progress_header_button -desktop c-button -primary -small" data-progress="button" data-progress-target="history">
                                                            <span>${this.translations['parcel-tracking.history']}</span>
                                                            <svg class="c-button_icon -right -visible" aria-hidden="true" focusable="false"><use xlink:href="assets/images/sprite.svg#more"></use></svg>
                                                            <svg class="c-button_icon -hidden" aria-hidden="true" focusable="false"><use xlink:href="assets/images/sprite.svg#minus"></use></svg>
                                                        </button>
                                                        ` : ''}
                                                    </div>
                                                </div>
                                                <div class="c-progress -inner">
                                                    <div class="c-progress_item">
                                                        ${commodity.rPoidsOring ? `
                                                        <div class="c-progress_item_label"><span>${this.translations['parcel-tracking.status.accepted']}</span></div>
                                                        <div class="c-progress_weight">
                                                            ${commodity.nTotalOrigin}&nbsp;${this.translations['parcel-tracking.piece-unit']} / ${commodity.rPoidsOring}&nbsp;kg
                                                        </div>
                                                        ` : ''}
                                                    </div>
                                                    <div class="c-progress_item">
                                                        <div class="c-progress_item_label"><span>${this.translations['parcel-tracking.status.transit']}</span></div>
                                                        <div class="c-progress_item_content">
                                                            ${commodity.rPoidsEnTransit ? `
                                                            <div class="c-progress_weight">
                                                                ${commodity.nTotalPieceEnTransit}&nbsp;${this.translations['parcel-tracking.piece-unit']} / ${commodity.rPoidsEnTransit}&nbsp;kg
                                                            </div>
                                                            `: ''}
                                                            ${ commodity.nTotalPieceEnTransit > 0 ? `
                                                            <button class="c-progress_header_button c-button -white -small" data-progress="button" data-progress-target="details">
                                                                <span>${this.translations['parcel-tracking.details']}</span>
                                                                <svg class="c-button_icon -right -visible" aria-hidden="true" focusable="false"><use xlink:href="assets/images/sprite.svg#more"></use></svg>
                                                                <svg class="c-button_icon -hidden" aria-hidden="true" focusable="false"><use xlink:href="assets/images/sprite.svg#minus"></use></svg>
                                                            </button>` : ''}
                                                        </div>
                                                    </div>
                                                    <div class="c-progress_item">
                                                        ${commodity.rPoidsRecu ? `
                                                        <div class="c-progress_item_label"><span>${this.translations['parcel-tracking.status.received']}</span></div>
                                                        <div class="c-progress_weight">
                                                            ${commodity.nTotalPieceRecu}&nbsp;${this.translations['parcel-tracking.piece-unit']} / ${commodity.rPoidsRecu}&nbsp;kg
                                                        </div>
                                                        ` : ''}
                                                    </div>
                                                    <div class="c-progress_item">
                                                        ${commodity.rPoidsRamasse ? `
                                                        <div class="c-progress_item_label"><span>${this.translations['parcel-tracking.status.picked-up']}</span></div>
                                                        <div class="c-progress_weight">
                                                            ${commodity.nTotalPieceRamasse}&nbsp;${this.translations['parcel-tracking.piece-unit']} / ${commodity.rPoidsRamasse}&nbsp;kg
                                                        </div>
                                                        ` : ''}
                                                    </div>
                                                </div>
                                                <button class="c-progress_header_button -mobile c-button -primary -small" data-progress="button" data-progress-target="history">
                                                    <span>${this.translations['parcel-tracking.history']}</span>
                                                    <svg class="c-button_icon -right -visible" aria-hidden="true" focusable="false"><use xlink:href="assets/images/sprite.svg#more"></use></svg>
                                                    <svg class="c-button_icon -hidden" aria-hidden="true" focusable="false"><use xlink:href="assets/images/sprite.svg#minus"></use></svg>
                                                </button>
                                            </div>
                                            ${commodity.ShipmentRegroupe.length > 0 ? `
                                            <section class="c-progress_details" data-progress="details">
                                                <div class="c-progress_details_wrap">
                                                    <h3 class="c-progress_heading c-heading -h5">${this.translations['parcel-tracking.transit-details']} (${commodity.ShipmentRegroupe.length})</h3>
                                                    <div class="c-progress_details_section">
                                                        <div>
                                                            ${commodity.ShipmentRegroupe.map((stateDetail) => `
                                                                <section class="c-progress_details_item">
                                                                    <div class="c-progress_icon">
                                                                        <svg aria-hidden="true" focusable="false"><use xlink:href="assets/images/sprite.svg#cargo-${stateDetail.StatusID}"></use></svg>
                                                                    </div>
                                                                    <div class="c-progress_details_content">
                                                                        <h4 class="c-heading -h6">${stateDetail.StatusDetails} (${this.translations['parcel-tracking.flight']} #${stateDetail.LegRefCode}${stateDetail.LegRefNo})</h4>
                                                                        <p>${this.translations['parcel-tracking.from'].capitalize()} ${stateDetail.OriginName} (${stateDetail.Origin.toUpperCase()}) ${this.translations['parcel-tracking.to']} ${stateDetail.DestinationName} (${stateDetail.Destination.toUpperCase()})</p>
                                                                        <time>${this.formatDate(stateDetail.FinalledDateTime)}</time>
                                                                    </div>
                                                                    ${stateDetail.Kgs
                                                                        ? `<div class="c-progress_weight">${stateDetail.Pieces}&nbsp;${this.translations['parcel-tracking.piece-unit']} / ${stateDetail.Kgs}&nbsp;kg</div>`
                                                                        : ''}
                                                                </section>
                                                            `).join('')}
                                                        </div>
                                                        <div class="c-progress_close">
                                                            <button class="c-progress_header_button c-button -primary -small" data-progress="close" data-progress-target="details">
                                                                <span>${this.translations['parcel-tracking.close']}</span>
                                                                <svg aria-hidden="true" focusable="false"><use xlink:href="assets/images/sprite.svg#minus"></use></svg>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </section>
                                            ` : ''}
                                            ${commodity.HistoriqueNew.length > 0 ? `
                                            <section class="c-progress_details" data-progress="history">
                                                <div class="c-progress_details_wrap">
                                                    <h3 class="c-progress_heading c-heading -h5">${this.translations['parcel-tracking.history']}</h3>
                                                    <div>
                                                        <div class="c-progress_details_section -line">
                                                            ${commodity.HistoriqueNew.map((historyDetail) => `
                                                                <section class="c-progress_details_item">
                                                                    <div class="c-progress_icon">
                                                                        <svg aria-hidden="true" focusable="false"><use xlink:href="assets/images/sprite.svg#cargo-${historyDetail.statuID}"></use></svg>
                                                                    </div>
                                                                    <div class="c-progress_details_content">
                                                                        <h4 class="c-heading -h6">${historyDetail.oSentence.m_Sentence}</h4>
                                                                        <time>${this.formatDate(`${historyDetail.m_sDateHistory}T${historyDetail.m_sHeureHistory}`)}</time>
                                                                    </div>
                                                                    ${historyDetail.m_rKg
                                                                        ? `<div class="c-progress_weight">${historyDetail.m_nPiece}&nbsp;${this.translations['parcel-tracking.piece-unit']} / ${historyDetail.m_rKg}&nbsp;kg</div>`
                                                                        : ''}
                                                                </section>
                                                            `).join('')}
                                                        </div>
                                                    </div>
                                                    <div class="c-progress_close">
                                                        <button class="c-progress_header_button c-button -primary -small" data-progress="close" data-progress-target="history">
                                                            <span>${this.translations['parcel-tracking.close']}</span>
                                                            <svg aria-hidden="true" focusable="false"><use xlink:href="assets/images/sprite.svg#minus"></use></svg>
                                                        </button>
                                                    </div>
                                                </div>
                                            </section>
                                            ` : ''}
                                        </div>
                                    `).join('')}
                                </div>
                            </section>
                        </div>
                    </div>
                    `}
                </div>
            </div>
        `;

        isDebug && console.groupEnd();

        return html;
    }

    /**
     * @param  {AWBResponseWrapper}        wrapper
     * @param  {TrackingNumber}            wrapper.number
     * @param  {AWBResponse|TrackingError} wrapper.response
     * @param  {?Error}                    [wrapper.error=null]
     * @param  {number}                    [index]
     * @param  {AWBResponseWrapper[]}      [responses]
     * @return {?Template}
     */
    renderShipmentNotFound({ number, response, error = null }) {
        isDebug && console.group('[App.Cargo.renderShipmentNotFound]', number);
        if (isDebug) {
            response && console.log('Response:', response);
            error && console.log('Error:', error);
        }

        const html = `
            <header class="o-section -margin-medium -border">
                <div class="o-section_container">
                    <p class="c-heading -h6">${this.translations['parcel-tracking.your-shipment.formatted']
                        ? this.translations['parcel-tracking.your-shipment.formatted'].replace(/\{\{ *trackingNumber *\}\}/i, number)
                        : this.translations['parcel-tracking.your-shipment']}</p>
                    <h1 class="c-header-page_heading -margin || c-heading -h2">
                        ${this.translations['parcel-tracking.shipment-not-found.title']}
                    </h1>
                    <p class="c-heading">
                        ${this.translations['parcel-tracking.shipment-not-found.text']}
                    </p>
                    <a href="${this.getData('error-cta')}" class="c-button -primary">
                        <span>${this.translations['parcel-tracking.shipment-not-found.cta-label']}</span>
                        <svg aria-hidden="true" focusable="false"><use xlink:href="assets/images/sprite.svg#arrow"></use></svg>
                    </a>
                </div>
            </header>
        `;

        isDebug && console.groupEnd();

        return html;
    }

    /**
     * @return {?Template}
     */
    renderTrackingResponseNotFound() {
        isDebug && console.group('[App.Cargo.renderTrackingResponseNotFound]');

        const html = `
            <header class="o-section -margin-medium -border">
                <div class="o-section_container">
                    <p class="c-heading -h6">${this.translations['parcel-tracking.your-shipment']}</p>
                    <h1 class="c-header-page_heading -margin || c-heading -h2">
                        ${this.translations['parcel-tracking.shipment-not-found.title']}
                    </h1>
                    <p class="c-heading">
                        ${this.translations['parcel-tracking.shipment-not-found.text']}
                    </p>
                    <a href="${this.getData('error-cta')}" class="c-button -primary">
                        <span>${this.translations['parcel-tracking.shipment-not-found.cta-label']}</span>
                        <svg aria-hidden="true" focusable="false"><use xlink:href="assets/images/sprite.svg#arrow"></use></svg>
                    </a>
                </div>
            </header>
        `;

        isDebug && console.groupEnd();

        return html;
    }

    toggleDetails(event) {
        event.preventDefault();

        const button  = event.curTarget;
        const section = this.parent('section', button);

        button.classList.toggle('is-open');
        this.$('more', section)[0].classList.toggle('is-open');
    }

    /**
     * @return {string}
     */
    getLang() {
        return this.getData('lang');
    }

    /**
     * @return {Intl.DateTimeFormat}
     */
    getDateTimeFormtter() {
        if (!this.dateTimeFormtter) {
            const locale = lang_formats[this.getLang()];

            this.dateTimeFormtter = new Intl.DateTimeFormat(locale, {
                dateStyle: 'long',
                timeStyle: 'short'
            });
        }

        return this.dateTimeFormtter;
    }
}
